import React from 'react';
import { Text, Box, Flex, RestrictedButton, InlineLink, Button } from '@fullstory/components';
import { DetailModalPropertiesTable, PropertyRow } from '@fullstory/events-ui';
import { FormattedMessage, useIntl } from 'react-intl';
import { DetailModalElementPropertyRulesTable } from '../../DetailModalElementPropertyRulesTable';
import { ElementPropertyRuleRecord } from '@fullstory/proto/fs/services/search-settings';
import {
  ElementPropertyRuleModal,
  ElementPropertyRuleModalSource,
  ElementPropertyRuleModalVariant,
} from '../../ElementPropertyRuleModal';
import {
  useElementPropertyRuleActions,
  fullstorySelectors,
  SpecObjectInfo,
} from '@fullstory/redux';
import { defaultNewElementPropertyRuleRecord } from '../../../utils';
import { generateRoutePath, SETTINGS_PROPERTIES } from '@fullstory/routes';
import { useSelector } from 'react-redux';
import { generateScrollToURL } from '@fullstory/library-ui';
import { NoDataSettingsIcon } from '@fullstory/graphics';

const { getOrgId } = fullstorySelectors;

type ElementIdentifiers = {
  id: string;
  name: string;
};

type ModalBodyPropertiesProps = {
  elementIdentifiers: ElementIdentifiers;
  properties: PropertyRow[];
  propertiesLoading: boolean;
  propertiesFailed: boolean;
  retryFailedProperties: () => void;
  elementPropertyRules: ElementPropertyRuleRecord[] | undefined;
  canModifyProperties: boolean;
  existingSpecObjects: SpecObjectInfo[];
};

export const ModalBodyProperties = ({
  elementIdentifiers,
  properties,
  propertiesLoading,
  propertiesFailed,
  retryFailedProperties,
  elementPropertyRules,
  canModifyProperties,
  existingSpecObjects,
}: ModalBodyPropertiesProps) => {
  const intl = useIntl();
  const orgId = useSelector(getOrgId);
  const {
    save: saveElementPropertyRule,
    archive: archiveElementPropertyRule,
    restore: restoreElementPropertyRule,
  } = useElementPropertyRuleActions();
  const [modalElementPropertyRuleRecord, setModalElementPropertyRuleRecord] =
    React.useState<ElementPropertyRuleRecord>();

  const propertyRulesRoute = generateRoutePath(SETTINGS_PROPERTIES, { orgId });
  const propertyRulesUrl = generateScrollToURL(propertyRulesRoute, 'propertyRules');

  return (
    <Box data-component="ModalBodyProperties">
      <Box paddingBottom={24}>
        {properties.length === 0 && elementPropertyRules?.length === 0 ? (
          <Box
            data-component="properties-table-empty-state"
            testId="properties-table-empty-state"
            padding={[64, 24]}
          >
            <Flex column>
              <NoDataSettingsIcon />
              <Box marginTop={20}>
                <Text appearance="heading2" align="center">
                  <FormattedMessage
                    defaultMessage="No properties were found for this element."
                    id="Vc0Tim"
                  />
                </Text>
                <Box marginTop={8}>
                  <Text appearance="bodyMedium" align="center">
                    <FormattedMessage
                      defaultMessage="Properties allow you to add additional information from your element to your events. <a></a>Use property rules to define these properties for the elements."
                      id="NX2TaB"
                      values={{
                        a: () => <br />,
                      }}
                    />
                  </Text>
                </Box>
              </Box>
              <Box marginTop={16}>
                <Button
                  variant="fill-primary"
                  onClick={() =>
                    setModalElementPropertyRuleRecord(
                      defaultNewElementPropertyRuleRecord({
                        namedElementId: elementIdentifiers.id,
                      }),
                    )
                  }
                >
                  <FormattedMessage defaultMessage="Create Property Rule" id="e1M5dH" />
                </Button>
              </Box>
            </Flex>
          </Box>
        ) : (
          <>
            <Text appearance="heading3">
              <FormattedMessage defaultMessage="Properties" id="aI80kg" />
            </Text>
            <Box marginTop={8} marginBottom={elementPropertyRules ? 24 : 0}>
              <DetailModalPropertiesTable
                properties={properties}
                connectionParent={elementIdentifiers.id}
                loading={propertiesLoading}
                failed={propertiesFailed}
                retryFailed={retryFailedProperties}
                canArchive={canModifyProperties}
                existingSpecObjects={existingSpecObjects}
              />
            </Box>
            {elementPropertyRules && (
              <Box display="flex" flexDirection="column" gap={8}>
                <Box display="flex" flexDirection="column" gap={4}>
                  <Flex alignItems="flex-start" justifyContent="space-between">
                    <Text appearance="heading3">
                      <FormattedMessage defaultMessage="Property Rules" id="nu+WLz" />
                    </Text>
                    {elementPropertyRules.length > 0 && (
                      <RestrictedButton
                        variant="outline"
                        size="x-small"
                        disabled={!canModifyProperties}
                        tooltip={intl.formatMessage({
                          defaultMessage:
                            'Your account does not have permission to configure Property Rules.',
                          id: 'Yoeiow',
                        })}
                        onClick={() =>
                          setModalElementPropertyRuleRecord(
                            defaultNewElementPropertyRuleRecord({
                              namedElementId: elementIdentifiers.id,
                            }),
                          )
                        }
                      >
                        <FormattedMessage defaultMessage="Create Property Rule" id="e1M5dH" />
                      </RestrictedButton>
                    )}
                  </Flex>
                  <Text appearance="bodySmall" tag="div">
                    <FormattedMessage
                      defaultMessage="Use Property Rules to extract data from your elements that can be used for deeper analysis."
                      id="/2ntvi"
                    />{' '}
                    <InlineLink
                      href="https://help.fullstory.com/hc/en-us/articles/24236341468311-Extracted-Element-Properties-Beta"
                      newTab
                    >
                      <FormattedMessage defaultMessage="Learn more" id="TdTXXf" />
                    </InlineLink>
                  </Text>
                  <Text appearance="bodySmall" tag="div">
                    <FormattedMessage
                      defaultMessage="Find all of your Property Rules in <a>Settings</a>."
                      id="K7Uifa"
                      values={{
                        a(text) {
                          return <InlineLink to={propertyRulesUrl}>{text}</InlineLink>;
                        },
                      }}
                    />
                  </Text>
                </Box>
                {elementPropertyRules.length > 0 ? (
                  <Box>
                    <DetailModalElementPropertyRulesTable
                      elementPropertyRules={elementPropertyRules}
                      canModify={canModifyProperties}
                      onRowClick={setModalElementPropertyRuleRecord}
                      onRowArchive={(id, archive) => {
                        if (archive) {
                          archiveElementPropertyRule(id);
                        } else {
                          restoreElementPropertyRule(id);
                        }
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    data-component="properties-table-empty-state"
                    testId="properties-table-empty-state"
                    padding={[64, 24]}
                  >
                    <Flex column>
                      <NoDataSettingsIcon />
                      <Box marginTop={20}>
                        <Text appearance="heading2" align="center">
                          <FormattedMessage
                            defaultMessage="No property rules were found for this element."
                            id="Zsh9iL"
                          />
                        </Text>
                        <Box marginTop={8}>
                          <Text appearance="bodyMedium" align="center">
                            <FormattedMessage
                              defaultMessage="Use property rules to define these properties for the elements."
                              id="2V0caQ"
                            />
                          </Text>
                        </Box>
                      </Box>
                      <Box marginTop={16}>
                        <Button
                          variant="fill-primary"
                          onClick={() =>
                            setModalElementPropertyRuleRecord(
                              defaultNewElementPropertyRuleRecord({
                                namedElementId: elementIdentifiers.id,
                              }),
                            )
                          }
                        >
                          <FormattedMessage defaultMessage="Create Property Rule" id="e1M5dH" />
                        </Button>
                      </Box>
                    </Flex>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
        {modalElementPropertyRuleRecord && (
          <ElementPropertyRuleModal
            elementPropertyRuleRecord={modalElementPropertyRuleRecord}
            onCloseSignal={() => setModalElementPropertyRuleRecord(undefined)}
            variant={
              modalElementPropertyRuleRecord.elementPropertyRule?.id
                ? ElementPropertyRuleModalVariant.view
                : ElementPropertyRuleModalVariant.new
            }
            onSave={elementPropertyRule => saveElementPropertyRule(elementPropertyRule)}
            onArchive={id => archiveElementPropertyRule(id)}
            onUnarchive={id => restoreElementPropertyRule(id)}
            source={ElementPropertyRuleModalSource.relatedSettings}
            sourceElementName={elementIdentifiers.name}
          />
        )}
      </Box>
    </Box>
  );
};
